import { useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Copy, Send, ArrowLeft, Loader2 } from 'lucide-react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { useCampania, useFiltersData } from '@/modules/multichannel/hooks/useCampanias'
import { CampaniaCreateForm } from '@/modules/multichannel/components/CampaniaCreateForm'
import type { CampaniaDetalle } from '@/modules/multichannel/types/campania'
import type { FiltersData } from '@/modules/multichannel/types/campania'

type TipoDuplicacion = 'normal' | 'reenvio'

function buildDuplicateData(
  original: CampaniaDetalle,
  tipo: TipoDuplicacion,
  filtersData: FiltersData | undefined
): CampaniaDetalle {
  const findId = (items: { id: number; nombre: string }[] | undefined, nombre: string): number | null =>
    items?.find((i) => i.nombre === nombre)?.id ?? null

  const base = { ...original, id: 0 }

  if (tipo === 'normal') {
    return {
      ...base,
      asunto: '',
      pre_encabezado: '',
      titulo_envio: '',
      contenido_html: '',
      archivo_url: null,
      fecha_publicacion: null,
      hora: null,
      reporte: '',
      notas: '',
      tipo_envio: '',
      tipo_envio_fk_id: null,
      estado_contenido: 'Pendiente',
      estado_envio: 'Pendiente',
      estado_reporte: 'Pendiente',
      estado_aprobacion: '',
      estado_contenido_fk_id: findId(filtersData?.estados_contenido, 'Pendiente'),
      estado_envio_fk_id: findId(filtersData?.estados_envio, 'Pendiente'),
      estado_reporte_fk_id: findId(filtersData?.estados_reporte, 'Pendiente'),
      estado_aprobacion_fk_id: null,
    }
  }

  // reenvio: conservar HTML/asunto, resetear fechas y estados de envío
  return {
    ...base,
    fecha_publicacion: null,
    hora: null,
    reporte: '',
    notas: '',
    tipo_envio: 'Reenvío',
    tipo_envio_fk_id: findId(filtersData?.tipos_envio, 'Reenvío') ?? original.tipo_envio_fk_id,
    estado_envio: 'Pendiente',
    estado_reporte: 'Pendiente',
    estado_aprobacion: original.requiere_aprobacion ? 'Aprobado' : '',
    estado_envio_fk_id: findId(filtersData?.estados_envio, 'Pendiente'),
    estado_reporte_fk_id: findId(filtersData?.estados_reporte, 'Pendiente'),
    estado_aprobacion_fk_id: original.requiere_aprobacion
      ? findId(filtersData?.estados_aprobacion, 'Aprobado')
      : null,
  }
}

export function CampaniaDuplicar() {
  useDocumentTitle('Duplicar Campaña')
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const tipo = searchParams.get('tipo') as TipoDuplicacion | null
  const campaniaId = parseInt(id || '0')

  const { data: campania, isLoading: isLoadingCampania } = useCampania(campaniaId)
  const { data: filtersData, isLoading: isLoadingFilters } = useFiltersData()

  const isLoading = isLoadingCampania || isLoadingFilters

  const initialData = useMemo(() => {
    if (!campania || !tipo) return undefined
    return buildDuplicateData(campania, tipo, filtersData)
  }, [campania, tipo, filtersData])

  if (!tipo) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Copy className="h-6 w-6" />
            Duplicar Campaña
          </h1>
          {isLoadingCampania ? (
            <p className="text-muted-foreground mt-1">Cargando...</p>
          ) : campania ? (
            <p className="text-muted-foreground mt-1">
              Selecciona el tipo de duplicación para <strong>{campania.nombre_campania}</strong>
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <Card
            className="cursor-pointer hover:border-teal-500 transition-colors border-2 border-transparent"
            onClick={() => setSearchParams({ tipo: 'normal' })}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-teal-500" />
                Duplicación Normal
              </CardTitle>
              <CardDescription>
                Copia la configuración de la campaña con el contenido en blanco.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✓ Mantiene laboratorio, marca y tipo</li>
                <li>✓ Mantiene configuración base</li>
                <li>✗ Asunto, HTML y fechas en blanco</li>
                <li>✗ Estados reseteados a Pendiente</li>
              </ul>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-purple-500 transition-colors border-2 border-transparent"
            onClick={() => setSearchParams({ tipo: 'reenvio' })}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-purple-500" />
                Reenvío
              </CardTitle>
              <CardDescription>
                Copia la campaña conservando el HTML y el asunto original.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✓ Conserva HTML y asunto</li>
                <li>✓ Tipo de envío: Reenvío</li>
                <li>✗ Fechas en blanco</li>
                <li>✗ Estado de envío: Pendiente</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isLoading || !initialData) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    )
  }

  if (!campania) {
    return (
      <div className="py-20 text-center text-muted-foreground">
        No se encontró la campaña
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <button
          onClick={() => setSearchParams({})}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Cambiar tipo de duplicación
        </button>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          {tipo === 'reenvio' ? <Send className="h-6 w-6" /> : <Copy className="h-6 w-6" />}
          {tipo === 'reenvio' ? 'Crear Reenvío' : 'Duplicar Campaña'}
        </h1>
        <p className="text-muted-foreground mt-1">
          Basado en: <strong>{campania.nombre_campania}</strong>
        </p>
      </div>

      <CampaniaCreateForm initialData={initialData} mode="duplicate" />
    </div>
  )
}
